import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { autoLogin, setToken, verifySession, handleLaravelRedirect, redeemConversationAccess } from '../api/auth';
import { emitAgentStatus } from '../api/realtime';
import { isShopifyConnected } from '../api/shopify';
import { getOnboardingSteps } from '../api/onboarding';
import { getStandardOnboardingSteps } from '../api/standardOnboarding';
import { getWordpressOnboardingSteps } from '../api/wordpressOnboarding';
import { updatePreferences } from '../api/userPreferences';
import { markFreshSignup } from '../utils/signupGate';

/**
 * AutoLoginPage — handles two modes:
 *
 *   /autologin?email=user@example.com   → calls POST /bot/api/auth/auto-login
 *   /autologin?token=eyJhbGci...        → stores token directly, verifies session
 *
 * After success, redirects to /onboarding (or /dashboard).
 */
const AutoLoginPage = ({ onLoginSuccess }) => {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  // A `redirect` query param lets a deep-link (e.g. the escalation email's
  // "Reply to Conversation" CTA) land on a specific page after auto-login.
  // Only same-origin internal paths are allowed (must start with a single "/")
  // to prevent open-redirects to an external site.
  const getSafeRedirect = () => {
    const r = searchParams.get('redirect');
    return r && /^\/(?!\/)/.test(r) ? r : null;
  };

  useEffect(() => {
    const token = searchParams.get('token');
    const email = searchParams.get('email');
    const act = searchParams.get('act');

    // Brand-new account just created via Google signup (backend appends ?new=1).
    // Flag it so the mobile desktop-gate engages on phones (no-op on desktop).
    // Login / deep-link returns (no `new`) never set this.
    if (searchParams.get('new') === '1') {
      markFreshSignup();
    }

    if (act) {
      // Conversation-access deep-link (escalation / ticket-transfer emails):
      // exchange the single-purpose token for a fresh session, then open the
      // conversation. Works logged-out and survives logout (it's not a session
      // token, so the logout blacklist can't kill it).
      (async () => {
        try {
          const sessionToken = await redeemConversationAccess(act);
          setToken(sessionToken);
          const verifiedUser = await verifySession();
          if (!verifiedUser) {
            setStatus('error');
            setErrorMsg('This link is invalid or has expired. Please request a new one.');
            return;
          }
          emitAgentStatus('online');
          onLoginSuccess(verifiedUser);
          navigate(getSafeRedirect() || '/dashboard', { replace: true });
        } catch (err) {
          console.error('Conversation auto-login failed:', err);
          setStatus('error');
          setErrorMsg(err.message || 'This link is invalid or has expired.');
        }
      })();
      return;
    }

    if (token) {
      // Token-based auto-login (used by Shopify app redirect and Google OAuth)
      (async () => {
        try {
          setToken(token);

          // Check if user data was passed in URL (from Google OAuth)
          const userParam = searchParams.get('user');
          let user = null;
          if (userParam) {
            try {
              user = JSON.parse(decodeURIComponent(userParam));
              localStorage.setItem('aerochat_user', JSON.stringify(user));
            } catch {
              console.warn('Failed to parse user from OAuth callback');
            }
          }

          // Verify session to get fresh user data
          const verifiedUser = await verifySession();
          if (!verifiedUser && !user) {
            setStatus('error');
            setErrorMsg('Invalid or expired token. Please try again.');
            return;
          }

          const finalUser = verifiedUser || user;

          // Defense-in-depth: any token-based SSO landing (Google, Shopify,
          // future providers) bypasses /login + /auto-login, so flip the
          // agent's online toggle here too. Backend handlers also flip it
          // where they can — this is the safety net for any future SSO
          // path added without remembering to update the backend.
          emitAgentStatus('online');

          // If platformType is missing, auto-detect from Shopify connection
          if (!finalUser.platformType) {
            try {
              const { connected } = await isShopifyConnected();
              if (connected) {
                finalUser.platformType = 'shopify';
                await updatePreferences({ platformType: 'shopify' }).catch(() => {});
                localStorage.setItem('aerochat_user', JSON.stringify(finalUser));
              }
            } catch { /* ignore */ }
          }

          onLoginSuccess(finalUser);

          // Honor an explicit deep-link target (e.g. a conversation) before the
          // platform-aware onboarding routing, so email CTAs land where intended.
          const redirectTo = getSafeRedirect();
          if (redirectTo) {
            navigate(redirectTo, { replace: true });
            return;
          }

          // Platform-aware routing for magic-link landings. Each platform
          // pre-checks onboarding completion so fully-onboarded merchants are
          // not bounced back to the Get Started page; only merchants with
          // outstanding required steps land on onboarding, everyone else falls
          // through to /dashboard. If the status check fails we also fall
          // through to the dashboard rather than trapping the user.
          // Shopify merchants never see the main-app onboarding page — setup
          // lives entirely in the embedded Shopify app now, so they always
          // land on the dashboard (falls through below). Other platforms keep
          // the onboarding bounce for incomplete required steps.
          if (finalUser.platformType === 'wordpress') {
            try {
              const steps = await getWordpressOnboardingSteps();
              const required = steps.filter(s => s.type === 'required');
              const allDone = required.length > 0 && required.every(s => s.status === 'completed');
              if (!allDone) {
                navigate('/getstarted/wordpress', { replace: true });
                return;
              }
            } catch { /* fall through to dashboard */ }
          } else if (finalUser.platformType === 'other' || finalUser.platformType === 'standard') {
            try {
              const steps = await getStandardOnboardingSteps();
              const required = steps.filter(s => s.type === 'required');
              const allDone = required.length > 0 && required.every(s => s.status === 'completed');
              if (!allDone) {
                navigate('/getstarted/standard', { replace: true });
                return;
              }
            } catch { /* fall through to dashboard */ }
          }

          if (finalUser.platformType) {
            navigate('/dashboard', { replace: true });
          } else {
            navigate('/platform-select', { replace: true });
          }
        } catch (err) {
          console.error('Token auto-login failed:', err);
          setStatus('error');
          setErrorMsg(err.message || 'Auto-login failed. Please try again.');
        }
      })();
      return;
    }

    if (!email) {
      setStatus('error');
      setErrorMsg('Missing login parameters.');
      return;
    }

    // Email-based auto-login (legacy)
    (async () => {
      try {
        const data = await autoLogin(email);
        if (handleLaravelRedirect(data)) return;
        const user = data.user || data;

        // If platformType is missing, auto-detect from Shopify connection
        if (!user.platformType) {
          try {
            const { connected } = await isShopifyConnected();
            if (connected) {
              user.platformType = 'shopify';
              await updatePreferences({ platformType: 'shopify' }).catch(() => {});
              localStorage.setItem('aerochat_user', JSON.stringify(user));
            }
          } catch { /* ignore */ }
        }

        onLoginSuccess(user);

        // Honor an explicit deep-link target before onboarding routing.
        const redirectTo = getSafeRedirect();
        if (redirectTo) {
          navigate(redirectTo, { replace: true });
          return;
        }

        // Platform-aware routing — same logic as the token path above.
        if (user.platformType === 'shopify') {
          try {
            const steps = await getOnboardingSteps();
            const required = steps.filter(s => s.type === 'required');
            const allDone = required.length > 0 && required.every(s => s.status === 'completed');
            if (!allDone) {
              navigate('/getstarted/shopify', { replace: true });
              return;
            }
          } catch { /* fall through to dashboard */ }
        } else if (user.platformType === 'wordpress') {
          try {
            const steps = await getWordpressOnboardingSteps();
            const required = steps.filter(s => s.type === 'required');
            const allDone = required.length > 0 && required.every(s => s.status === 'completed');
            if (!allDone) {
              navigate('/getstarted/wordpress', { replace: true });
              return;
            }
          } catch { /* fall through to dashboard */ }
        } else if (user.platformType === 'other' || user.platformType === 'standard') {
          try {
            const steps = await getStandardOnboardingSteps();
            const required = steps.filter(s => s.type === 'required');
            const allDone = required.length > 0 && required.every(s => s.status === 'completed');
            if (!allDone) {
              navigate('/getstarted/standard', { replace: true });
              return;
            }
          } catch { /* fall through to dashboard */ }
        }

        if (user.platformType) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/platform-select', { replace: true });
        }
      } catch (err) {
        console.error('Auto-login failed:', err);
        setStatus('error');
        setErrorMsg(err.message || 'Auto-login failed. Please try again.');
      }
    })();
  }, []);

  if (status === 'error') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h2 style={styles.title}>Auto-login Failed</h2>
          <p style={styles.message}>{errorMsg}</p>
          <button style={styles.button} onClick={() => navigate('/login', { replace: true })}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.spinner} />
        <h2 style={styles.title}>Signing you in...</h2>
        <p style={styles.message}>Please wait while we log you in.</p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: '#FEFCFA',
  },
  card: {
    textAlign: 'center',
    padding: '48px 40px',
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    maxWidth: '400px',
    width: '100%',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e5e7eb',
    borderTop: '4px solid #e5375a',
    borderRadius: '50%',
    animation: 'autologin-spin 0.8s linear infinite',
    margin: '0 auto 20px',
  },
  errorIcon: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#111827',
    margin: '0 0 8px',
  },
  message: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 24px',
    lineHeight: 1.5,
  },
  button: {
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#fff',
    background: '#e5375a',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
};

// Inject spinner keyframes
if (typeof document !== 'undefined' && !document.getElementById('autologin-spin-style')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'autologin-spin-style';
  styleEl.textContent = '@keyframes autologin-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(styleEl);
}

export default AutoLoginPage;
